import { Component, OnInit, Input, OnChanges, ViewChild, ElementRef } from '@angular/core'
import { Store, select } from '@ngrx/store'
import { RootStoreState } from '../../../../../root-store/root-index'
import * as fromConfigSelectors from '../../../../../root-store/config-store/config.selectors'
import { ConfirmationService, SelectItem } from 'primeng/api'
import { NgForm } from '@angular/forms'
import { map, take } from 'rxjs/operators'
import { Attribute } from '../../../../../models/attribute'
import { TableData } from '../../../models/table-data'
import { EntityService } from '../../../services/entity.service'
import { SettingsService } from '../../../../settings/services/settings.service'
import EntityUrlsUtils from '../../../../../utils/entity-urls.util'
import { Field } from '../../../models/field'
import { InputText } from 'primeng/inputtext'
import { RichTextEditorComponent } from '../../../../misc'
import { DialogService } from 'primeng/dynamicdialog'
import StringUtils from '../../../../../utils/string.utils'

@Component({
  selector: 'jml-lib-entity-attributes',
  templateUrl: './entity-attributes.component.html',
  styleUrls: ['./entity-attributes.component.scss']
})
export class EntityAttributesComponent implements OnInit, OnChanges {
  @Input() ownerType: string
  @Input() ownerId: number

  @ViewChild('table', { read: ElementRef, static: false }) tableRef: ElementRef

  attributes: Attribute[]
  displayAddAttribute = false
  dialogAttribute = new Attribute()
  attrNames: object[] = []
  refDtoRepr: string
  dtoConfigs: SelectItem[]
  displayEntitySelectionDialog_add = false
  displayEntitySelectionDialog_update = false
  entitySelectionTableData: TableData
  updatedRowData: Attribute
  header: string
  currency: string
  icon: any
  dialogUpdate: boolean

  //create custom localization for p-calendar component
  de: any

  types = [
    { label: 'Boolean', value: 'Boolean' },
    { label: 'Color', value: 'Color' },
    { label: 'Currency', value: 'Currency' },
    { label: 'Date', value: 'Date' },
    { label: 'DateTime', value: 'DateTime' },
    { label: 'Float', value: 'Float' },
    { label: 'Icon', value: 'Icon' },
    { label: 'Integer', value: 'Integer' },
    { label: 'Multiline', value: 'Multiline' },
    { label: 'Reference', value: 'Reference' },
    { label: 'String', value: 'String' }
  ]

  filterTags = StringUtils.filterTags

  constructor(
    private entityService: EntityService,
    private store$: Store<RootStoreState.State>,
    private settingsService: SettingsService,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService
  ) {}

  ngOnInit() {
    //TODO: i18n verwenden! https://blog.ninja-squad.com/2019/12/10/angular-localize/
    this.de = {
      firstDayOfWeek: 1,
      dayNames: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
      dayNamesShort: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
      dayNamesMin: ['S', 'M', 'D', 'M', 'D', 'F', 'S'],
      monthNames: [
        'Januar',
        'Februar',
        'März',
        'April',
        'Mai',
        'Juni',
        'Juli',
        'August',
        'September',
        'Okotober',
        'November',
        'Dezember'
      ],
      monthNamesShort: ['Jan', 'Feb', 'Mrz', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
      today: 'Heute',
      clear: 'Verwerfen'
    }

    // get all dtos for type "Reference"
    const configurations$ = this.store$.pipe(select(fromConfigSelectors.selectConfigs))
    configurations$.subscribe((configs) => {
      if (configs) {
        this.dtoConfigs = Object.values(configs).map((config) =>
          this.configToSelectItem(config.singleDisplayName, config.type)
        )
      }
    })

    // get Currency from settings
    this.settingsService
      .getSetting('CURRENCY')
      .pipe(map((setting) => setting.value))
      .subscribe((currency) => (this.currency = currency))
  }

  afterTableInit() {
    this.tableRef.nativeElement.addEventListener(
      'click',
      (event: MouseEvent) => {
        if (event.target['id'] === 'cancelRowSelection') {
          // If the target indicates a cancellation of a row selection event, stop its propagation to prevent row selection
          event.preventDefault()
          event.stopPropagation()
          document.dispatchEvent(new MouseEvent('click'))
          // dispatch new event to target
          event.target.dispatchEvent(new CustomEvent('edit'))
        }
      },
      true
    )
  }

  ngOnChanges() {
    this.updateAttributes()
  }

  openAttributeDialog(attribute?: Attribute) {
    if (attribute) {
      this.dialogAttribute = Object.assign({}, attribute)
      this.dialogUpdate = true
    } else {
      this.refDtoRepr = ''
      this.dialogAttribute = new Attribute()
      this.dialogAttribute.ownerType = this.ownerType
      this.dialogAttribute.ownerId = this.ownerId
      this.dialogUpdate = false
    }
    this.displayAddAttribute = true
  }

  isEntityReference(field: Field) {
    return Field.isReference(field) && !Field.isCatalogueEntry(field)
  }

  configToSelectItem(name: string, type: string): SelectItem {
    return { label: name, value: type }
  }

  updateAttributes() {
    this.entityService.getAttributes(this.ownerType, this.ownerId).subscribe((response) => {
      this.attributes = <Attribute[]>(<unknown>response)
      this.attributes.find((attr) => attr.referenceType)
    })
  }

  addNewAttribute() {
    this.displayAddAttribute = false
    this.entityService.addAttribute(this.dialogAttribute).subscribe((attribute) => this.attributes.push(attribute))
  }

  updateAttribute() {
    this.displayAddAttribute = false
    const attributeIndex = this.attributes.findIndex((a) => a.id === this.dialogAttribute.id)
    this.entityService
      .updateAttribute(this.dialogAttribute)
      .subscribe((attr) => (this.attributes[attributeIndex] = attr))
  }

  onRowDelete(attribute: Attribute) {
    this.confirmationService.confirm({
      header: 'Attribut löschen',
      message: 'Sind Sie sicher, dass Sie dieses Attribut löschen wollen?',
      key: 'global',
      accept: () => {
        this.entityService
          .removeAttribute(attribute['id'])
          .subscribe(() => (this.attributes = this.attributes.filter((element) => element['id'] !== attribute['id'])))
      }
    })
  }

  onRowEditSave(attribute: Attribute, typeCell, referenceData?) {
    if (typeCell) {
      typeCell.isEdited = false
    }

    // If the attribute is a Reference we need to set the referenceId from the referenceData since the reference cell does not operate on attributes
    if (!!referenceData) {
      attribute.referenceId = referenceData['data']['attribute']?.id
    }

    this.updatedRowData = attribute
    this.entityService
      .updateAttribute(attribute)
      .subscribe((attr) => Attribute.copyFrom(<Attribute>attr, this.updatedRowData))
  }

  openEntitySelectionDialog_add(type: string) {
    this.entitySelectionTableData = new TableData(type)
      .isEntitySelectionDialog()
      .hideHeader()
      .hideAttachments()
      .hideButtons()
      .setScrollable()
      .setScrollHeight(window.innerHeight - 350 + 'px')
      .disableInlineEdit()
    this.header = `${this.dialogAttribute.name ? this.dialogAttribute.name : this.displayNameFromType(type)} auswählen`
    this.displayEntitySelectionDialog_add = true
  }

  openEntitySelectionDialog_update(type: string, attribute: Attribute) {
    this.entitySelectionTableData = new TableData(type)
      .isEntitySelectionDialog()
      .hideHeader()
      .hideAttachments()
      .hideButtons()
      .setScrollable()
      .setScrollHeight(window.innerHeight - 350 + 'px')
      .disableInlineEdit()
    this.updatedRowData = attribute
    this.header = `${this.dialogAttribute.name ? this.dialogAttribute.name : this.displayNameFromType(type)} auswählen`
    this.displayEntitySelectionDialog_update = true
  }

  entitySelected_add(entity: any, form: NgForm) {
    form.controls['Wert'].setValue(entity['_repr_'], { emitEvent: true })
    this.dialogAttribute.referenceId = entity['id']
    this.displayEntitySelectionDialog_add = false
  }

  entitySelected_update(entity: any, form: NgForm) {
    this.updatedRowData.value = entity['_repr_']
    this.updatedRowData.referenceId = entity['id']
    this.displayEntitySelectionDialog_update = false
  }

  onIconPickedAdd(icon) {
    if (icon !== 'fas fa-user') {
      this.dialogAttribute.stringValue = icon
    }
  }

  generateRouteUrl = (entityType: string): string[] => EntityUrlsUtils.generateUrlForEntityType(entityType)
  generateRouteQueryParams = (entityType: string, id: number): Object =>
    EntityUrlsUtils.generateQueryParamsForEntityType({ entityType, id })

  openMultilineEditor(input: InputText) {
    ;(<HTMLInputElement>(<unknown>input)).blur() // Prevents ExpressionChangedAfterItHasBeenCheckedError

    const dialogRef = this.dialogService.open(RichTextEditorComponent, {
      data: {
        text: this.dialogAttribute.stringValue,
        saveBtnLbl: 'Übernehmen'
      },
      header: 'abc',
      draggable: true,
      width: '80%'
    })

    dialogRef.onClose.pipe(take(1)).subscribe((text: string) => {
      if (text !== undefined) {
        this.dialogAttribute.stringValue = text
      }
    })
  }

  displayNameFromType(type: string) {
    return this.dtoConfigs.find((item) => item.value === type)?.label
  }
}
